import MicOffIcon from '@mui/icons-material/MicOff';
import ScreenShareIcon from '@mui/icons-material/ScreenShare';
import SettingsIcon from '@mui/icons-material/Settings';
import ShareIcon from '@mui/icons-material/Share';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import classNames from 'classnames';

import {videoProvider} from '@/services/videoService';
import copyToClipboard from '@/utils/copyToClipboard';
import {vidyoProviderLogs} from '@/utils/general';
import {generalNotify} from '@/utils/notifyMessages';

import {CustomSelect} from '../SettingsDevices';
import {SelectOption} from '../types';

import styles from '../videoCall.module.scss';

export type CallControlsProps = {
    isConnected: boolean;
    currentMeetingId: string;
    cameraBlocked: boolean;
    micBlocked: boolean;
    isCameraOn: boolean;
    isMicrophoneOn: boolean;
    isSpeakerOn: boolean;
    bgNoiseOn: boolean;
    curCamera: SelectOption | null;
    curMic: SelectOption | null;
    curSpeaker: SelectOption | null;
    cameraList: SelectOption[];
    micList: SelectOption[];
    speakerList: SelectOption[];
    onEndCall: () => void;
    onCameraToggle: () => void;
    onMicToggle: () => void;
    onSpeakerToggle: () => void;
    onBgNoiseToggle: () => Promise<void>;
    onScreenShare: () => Promise<void>;
    onOpenSettings: () => void;
    onCameraChange: (cam: SelectOption | undefined) => Promise<void>;
    onMicChange: (mic: SelectOption | undefined) => Promise<void>;
    onSpeakerChange: (speaker: SelectOption | undefined) => Promise<void>;
};

export const CallControls = ({
    isConnected,
    currentMeetingId,
    cameraBlocked,
    micBlocked,
    isCameraOn,
    isMicrophoneOn,
    isSpeakerOn,
    // bgNoiseOn,
    curCamera,
    curMic,
    curSpeaker,
    cameraList,
    micList,
    speakerList,
    onEndCall,
    onCameraToggle,
    onMicToggle,
    onSpeakerToggle,
    // onBgNoiseToggle,
    onScreenShare,
    onOpenSettings,
    onCameraChange,
    onMicChange,
    onSpeakerChange,
}: CallControlsProps) => (
    <div className={styles.toolbar}>
        <div id="toolbarCenter" className={styles.toolbarCenter}>
            <button
                id="joinLeaveButton"
                title="Join Conference"
                className={classNames(styles.toolbarButton, isConnected ? 'callEnd' : 'callStart')}
                onClick={onEndCall}
            />
            <button
                id="cameraButton"
                title={cameraBlocked ? 'Camera access is blocked' : 'Camera Privacy'}
                className={classNames(
                    styles.toolbarButton,
                    isCameraOn ? 'cameraOn' : 'cameraOff',
                    cameraBlocked && styles.toolbarButtonBlocked,
                )}
                onClick={onCameraToggle}
            >
                {cameraBlocked && <VideocamOffIcon />}
            </button>
            <CustomSelect
                dropdownOnly
                label="Camera"
                selectedValue={curCamera}
                options={cameraList}
                onChange={(e) => onCameraChange(cameraList.find((c) => c.value === e.target.value))}
            />
            <button
                id="microphoneButton"
                title={micBlocked ? 'Microphone access is blocked' : 'Microphone Privacy'}
                className={classNames(
                    styles.toolbarButton,
                    isMicrophoneOn ? 'microphoneOn' : 'microphoneOff',
                    micBlocked && styles.toolbarButtonBlocked,
                )}
                onClick={onMicToggle}
            >
                {micBlocked && <MicOffIcon />}
            </button>
            <CustomSelect
                dropdownOnly
                label="Microphone"
                selectedValue={curMic}
                options={micList}
                onChange={(e) => onMicChange(micList.find((m) => m.value === e.target.value))}
            />
            <button
                id="speakerButton"
                title="Speaker Privacy"
                className={classNames(styles.toolbarButton, isSpeakerOn ? 'speakerOn' : 'speakerOff')}
                onClick={onSpeakerToggle}
            />
            <CustomSelect
                dropdownOnly
                label="Speaker"
                selectedValue={curSpeaker}
                options={speakerList}
                onChange={(e) => onSpeakerChange(speakerList.find((s) => s.value === e.target.value))}
            />
            <button
                id="copyMeetingLinkButton"
                title="Copy Meeting Link"
                className={classNames(styles.toolbarButton)}
                onClick={() => {
                    copyToClipboard(
                        videoProvider.getSharedLink({
                            meetingId: currentMeetingId,
                            portalUrl: window.VIDEO_SCHEDULED_ACCESS,
                        }),
                    )
                        .then(() => {
                            generalNotify({
                                title: 'Success',
                                message: 'Meeting link copied to clipboard',
                                status: 'success',
                            });
                        })
                        .catch((err) => {
                            vidyoProviderLogs({
                                message: 'Failed to copy meeting link',
                                eventType: 'features',
                                status: 'error',
                                error: err,
                            });
                        });
                }}
            >
                <ShareIcon />
            </button>
            <button
                id="shareScreenButton"
                title="Share Screen"
                className={classNames(styles.toolbarButton)}
                onClick={() => void onScreenShare()}
            >
                <ScreenShareIcon />
            </button>
            {/* <button
                id="bgNoiseButton"
                title={`Background Noise: ${bgNoiseOn ? 'ON' : 'OFF'}`}
                className={classNames(styles.toolbarButton)}
                onClick={() => void onBgNoiseToggle()}
            >
                {bgNoiseOn ? <NoiseAware /> : <NoiseControlOff />}
            </button> */}
            <button className={classNames(styles.toolbarButton)} onClick={onOpenSettings}>
                <SettingsIcon />
            </button>
        </div>
    </div>
);
